import React from 'react';
import { METRIC_LABELS, type MarkerStatus, type MetricType } from './arcMetrics';
import { uiBtnSegment } from '../../ui/buttonClasses';

const STATUS_CONSOLE: Record<MarkerStatus, { code: string; lampClass: string; textClass: string }> =
  {
    good: {
      code: 'OK',
      lampClass: 'bg-rowing-green shadow-[0_0_6px_rgba(34,197,94,0.55)]',
      textClass: 'text-rowing-green',
    },
    caution: {
      code: 'EDGE',
      lampClass: 'bg-rowing-yellow shadow-[0_0_6px_rgba(234,179,8,0.45)]',
      textClass: 'text-rowing-yellow',
    },
    bad: {
      code: 'OUT',
      lampClass: 'bg-rowing-red shadow-[0_0_6px_rgba(220,38,38,0.5)]',
      textClass: 'text-rowing-red',
    },
    na: {
      code: 'N/A',
      lampClass: 'bg-rowing-gray-400',
      textClass: 'text-content-muted',
    },
  };

/** Inset readout panel — even border + subtle status wash. */
const READOUT_PANEL: Record<MarkerStatus, string> = {
  good: 'border-rowing-green/35 from-rowing-green/[0.07] ring-rowing-green/12',
  caution: 'border-rowing-yellow/40 from-rowing-yellow/[0.08] ring-rowing-yellow/12',
  bad: 'border-rowing-red/40 from-rowing-red/[0.09] ring-rowing-red/15',
  na: 'border-border from-surface-muted/20 ring-border/30',
};

export interface ArcMetricConsoleProps {
  selectedMetric: MetricType;
  status: MarkerStatus;
  strokeNumber: number;
  detail: string;
  /** Session % in target for the selected timeline metric (same basis as header). */
  sessionInTargetPercent: number | null;
  prevFlaggedStrokeNumber: number | null;
  nextFlaggedStrokeNumber: number | null;
  onJumpToStroke?: (strokeNumber: number) => void;
}

const formatSessionPercent = (value: number | null) =>
  value == null ? '—' : `${Math.round(value)}%`;

const ArcMetricConsole: React.FC<ArcMetricConsoleProps> = ({
  selectedMetric,
  status,
  strokeNumber,
  detail,
  sessionInTargetPercent,
  prevFlaggedStrokeNumber,
  nextFlaggedStrokeNumber,
  onJumpToStroke,
}) => {
  const statusUi = STATUS_CONSOLE[status];
  const showIssueNav =
    onJumpToStroke != null && (prevFlaggedStrokeNumber != null || nextFlaggedStrokeNumber != null);

  return (
    <div
      className="rounded-lg border border-border bg-surface p-2.5 shadow-sm sm:p-3"
      role="region"
      aria-label="Metric console"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="ui-segment-group min-w-0" aria-hidden>
          <span className="ui-btn ui-btn--sm ui-btn--segment ui-btn--toggle-active pointer-events-none max-w-[9rem] truncate rounded-sm">
            {METRIC_LABELS[selectedMetric]}
          </span>
          <span
            className="ui-btn ui-btn--sm ui-btn--segment ui-btn--toggle pointer-events-none rounded-sm tabular-nums"
            title={`Session in target for ${METRIC_LABELS[selectedMetric]}`}
          >
            STR {strokeNumber}
            <span className="text-content-muted"> · </span>
            {formatSessionPercent(sessionInTargetPercent)}
          </span>
        </div>

        <div
          className="flex shrink-0 items-center gap-1.5 rounded-sm border border-border bg-surface-muted/80 px-2 py-1"
          aria-label={`Status ${statusUi.code}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${statusUi.lampClass}`} aria-hidden />
          <span className={`text-[10px] font-bold tracking-widest ${statusUi.textClass}`}>
            {statusUi.code}
          </span>
        </div>
      </div>

      <div
        className={`mt-2 rounded-md border bg-gradient-to-br via-canvas to-canvas px-2.5 py-2 font-mono text-[11px] leading-relaxed text-content-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.05),inset_0_2px_6px_rgba(0,0,0,0.14)] ring-1 ring-inset ${READOUT_PANEL[status]}`}
        role="status"
        aria-live="polite"
      >
        {detail}
      </div>

      {showIssueNav && (
        <div
          className="mt-2 flex items-center justify-between gap-2"
          role="group"
          aria-label="Navigate between flagged strokes"
        >
          {prevFlaggedStrokeNumber != null ? (
            <button
              type="button"
              onClick={() => onJumpToStroke(prevFlaggedStrokeNumber)}
              className={uiBtnSegment(false, 'only')}
              aria-label={`Go to previous flagged stroke ${prevFlaggedStrokeNumber}`}
            >
              ← Stroke {prevFlaggedStrokeNumber}
            </button>
          ) : (
            <span className="flex-1" aria-hidden />
          )}
          {nextFlaggedStrokeNumber != null ? (
            <button
              type="button"
              onClick={() => onJumpToStroke(nextFlaggedStrokeNumber)}
              className={`${uiBtnSegment(false, 'only')} ${prevFlaggedStrokeNumber == null ? '' : 'ml-auto'}`}
              aria-label={`Go to next flagged stroke ${nextFlaggedStrokeNumber}`}
            >
              → Stroke {nextFlaggedStrokeNumber}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ArcMetricConsole;
